import { z } from 'zod';

/**
 * Provenance of an op. The taxonomy widens incrementally:
 *
 * - `gm` — the human GM (default authoring identity at Walking Skeleton).
 * - `system` — the substrate itself (migrations, automations).
 * - `llm` — LLM-generated content via the AI Gateway routing chokepoint
 *   (`11-tech-stack.md` §LLM, `04-llm-role.md`). The runtime guard in
 *   `apps/do-workspace/src/append.ts` + the CI scanner enforce the
 *   "LLM never writes canon, LLM never writes pending" invariant; this
 *   schema is the structural anchor. III.C.4 (#72) extends `llm` with
 *   provenance fields:
 *     - `provider` — the `llm_providers.adapter_key` ('anthropic', 'openai', …)
 *     - `model`    — the `llm_models.model_key` ('claude-sonnet-4-6', …)
 *     - `request_id` — provider-side request id, when surfaced
 *     - `prompt_hash` — SHA-256 prefix of the composed prompt (16 hex chars)
 *
 *   These fields are IMMUTABLE — once an op lands, they are never
 *   rewritten. The op envelope's `author` is part of the signed wire
 *   format; mutating it after acceptance would invalidate the chain.
 *
 * Future kinds (PC / NPC / external integration) extend the enum without
 * breaking the schema — pre-existing rows back-fill at projection time
 * the same way the status / owner defaults do.
 */
export type Author =
  | { kind: 'gm'; id?: string }
  | { kind: 'system'; id?: string }
  | { kind: 'player'; id: string }
  | LlmAuthor;

export interface LlmAuthor {
  kind: 'llm';
  /** Provider adapter key (e.g. 'anthropic'). */
  provider: string;
  /** Provider-side model key (e.g. 'claude-sonnet-4-6'). */
  model: string;
  /** Provider request id, when the adapter surfaces one. */
  request_id?: string;
  /** SHA-256 prefix (16 hex chars) of the composed prompt, for provenance. */
  prompt_hash?: string;
  /**
   * The `llm_trace.id` of the generation that produced this op — llm-v2
   * (#159, ADR-0003 D5). Closes the cost→quality loop: the draft fact's
   * terminal GM action (ratify / reject / edit) joins back to the trace's
   * per-step cost + prompt-template version.
   */
  trace_id?: string;
  /** Back-compat — historical LLM ops carry just `kind: 'llm'`. */
  id?: string;
}

export const LlmAuthorSchema = z
  .object({
    kind: z.literal('llm'),
    provider: z.string().min(1),
    model: z.string().min(1),
    request_id: z.string().min(1).optional(),
    prompt_hash: z.string().min(1).optional(),
    trace_id: z.string().min(1).optional(),
    id: z.string().min(1).optional(),
  })
  .strict();

/**
 * Schema for `Author`. Discriminated on `kind`. We accept the legacy
 * minimal-LLM shape (`{ kind: 'llm' }` with no provider/model) via a
 * lenient lane for ops that landed before #72 — historical rows in the
 * op log were never required to carry the provenance fields; the
 * projection treats absent fields as `unknown` rather than failing the
 * fold.
 */
export const AuthorSchema = z.union([
  z
    .object({
      kind: z.literal('gm'),
      id: z.string().min(1).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('system'),
      id: z.string().min(1).optional(),
    })
    .strict(),
  // II.B.5 / §7.2 — a controlling player's direct write on their PC's
  // `player_authority` predicates. The DO STAMPS this server-side from the
  // authenticated viewer (`user:<id>`); the client-supplied author is never
  // trusted for authorization (the op-accept gate re-derives it).
  z
    .object({
      kind: z.literal('player'),
      id: z.string().min(1),
    })
    .strict(),
  LlmAuthorSchema,
  // Legacy LLM shape — pre-#72 ops. Lenient lane: kind only.
  z
    .object({
      kind: z.literal('llm'),
      id: z.string().min(1).optional(),
    })
    .strict(),
]);

/**
 * Build an LLM Author with provenance — III.C.4 (#72). Callers in
 * `apps/worker/src/ai/gateway.ts` mint this for the gateway response;
 * the client wraps it onto its `SubmittedOp` when the GM presses
 * "Accept as draft".
 */
export function llmAuthor(args: {
  provider: string;
  model: string;
  request_id?: string;
  prompt_hash?: string;
  trace_id?: string;
}): LlmAuthor {
  const out: LlmAuthor = {
    kind: 'llm',
    provider: args.provider,
    model: args.model,
  };
  if (args.request_id !== undefined) out.request_id = args.request_id;
  if (args.prompt_hash !== undefined) out.prompt_hash = args.prompt_hash;
  if (args.trace_id !== undefined) out.trace_id = args.trace_id;
  return out;
}
